import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Radar, Truck, Users, AlertOctagon, Globe, Activity, Route as RouteIcon, Sparkles, X } from "lucide-react";
import api from "@/lib/api";
import { PageHeader, useFetch, Spinner, StatCard } from "@/components/portal/ui";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import SouthernAfricaMap from "@/components/SouthernAfricaMap";

function fmt(t) { return t ? new Date(t).toLocaleString("en-ZA", { timeStyle: "short", dateStyle: "short" }) : ""; }

const DIST = [
  { k: "normal", l: "Normal", c: "#16A34A" }, { k: "at_risk", l: "At Risk", c: "#EAB308" },
  { k: "delayed", l: "Delayed", c: "#F97316" }, { k: "exception", l: "Exception", c: "#DC2626" },
  { k: "delivered", l: "Delivered", c: "#2563EB" },
];

export default function ControlTower() {
  const nav = useNavigate();
  const { data, loading } = useFetch(() => api.get("/ops/control-tower").then((r) => r.data));
  const cityData = useFetch(() => api.get("/public/cities").then((r) => r.data.coords)).data || {};
  const [selected, setSelected] = useState(null);
  const [live, setLive] = useState({});
  const [connected, setConnected] = useState(false);
  const [depot, setDepot] = useState("Johannesburg");
  const [routePlan, setRoutePlan] = useState(null);
  const [optimizing, setOptimizing] = useState(false);

  const optimizeOverlay = async () => {
    setOptimizing(true);
    try {
      const r = await api.post("/ops/route-optimize", { depot });
      setRoutePlan(r.data);
      toast.success(`Optimised ${r.data.stop_count} stops · saved ${r.data.saved_km} km`);
    } catch { toast.error("Optimisation failed"); }
    setOptimizing(false);
  };

  useEffect(() => {
    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws");
    let ws;
    try {
      ws = new WebSocket(`${base}/api/ws/positions`);
      ws.onopen = () => setConnected(true);
      ws.onclose = () => setConnected(false);
      ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data);
          if (msg.type === "positions") {
            setLive((prev) => {
              const next = { ...prev };
              msg.shipments.forEach((sh) => { next[sh.id] = sh; });
              return next;
            });
          }
        } catch { /* ignore */ }
      };
    } catch { /* ignore */ }
    return () => {
      try {
        if (!ws) return;
        if (ws.readyState === WebSocket.OPEN) ws.close();
        else ws.addEventListener("open", () => { try { ws.close(); } catch { /* ignore */ } });
      } catch { /* ignore */ }
    };
  }, []);

  if (loading) return <Spinner />;
  const s = data.summary;
  const mergedShips = data.all_shipments.map((sh) => (live[sh.id] ? { ...sh, ...live[sh.id] } : sh));

  return (
    <div>
      <PageHeader title="Operations Control Tower" subtitle="Live operational overview · Southern Africa">
        <span data-testid="ct-live-indicator" className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold ${connected ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300" : "bg-slate-100 text-slate-600 dark:bg-slate-500/15"}`}>
          <span className={`relative w-2 h-2 rounded-full ${connected ? "bg-emerald-500 pulse-node" : "bg-slate-400"}`} /> {connected ? "LIVE" : "Connecting…"}
        </span>
      </PageHeader>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <StatCard testid="ct-active" label="Active Shipments" value={s.total_active} icon={Radar} />
        <StatCard testid="ct-moving" label="Vehicles Moving" value={s.vehicles_moving} icon={Truck} accent="text-blue-600" />
        <StatCard testid="ct-drivers" label="Drivers On Duty" value={s.drivers_on_duty} icon={Users} accent="text-emerald-600" />
        <StatCard testid="ct-priority" label="High Priority" value={s.high_priority} icon={AlertOctagon} accent="text-red-600" />
        <StatCard testid="ct-border" label="Cross-Border" value={s.cross_border} icon={Globe} accent="text-orange-600" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="mb-3 flex flex-wrap items-center gap-2 rounded-xl border border-border bg-card p-3" data-testid="ct-route-optimizer">
            <span className="flex items-center gap-1.5 text-sm font-heading font-bold"><RouteIcon size={16} className="text-primary" /> Route overlay</span>
            <select data-testid="ct-depot" value={depot} onChange={(e) => setDepot(e.target.value)}
              className="h-9 rounded-md border border-border bg-background px-2.5 text-sm">
              {Object.keys(cityData).map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <Button size="sm" className="bg-primary hover:bg-red-700 h-9" onClick={optimizeOverlay} disabled={optimizing} data-testid="ct-optimize">
              <Sparkles size={14} className="mr-1.5" /> {optimizing ? "Optimising…" : "Optimise & overlay"}
            </Button>
            {routePlan && (
              <>
                <span className="text-xs text-muted-foreground">{routePlan.stop_count} stops · {routePlan.optimized_km} km{routePlan.road_snapped ? " · road-snapped" : ""} · saved {routePlan.saved_km} km</span>
                <Button size="sm" variant="ghost" className="h-9 text-xs ml-auto" onClick={() => setRoutePlan(null)} data-testid="ct-clear-route"><X size={14} className="mr-1" /> Clear</Button>
              </>
            )}
          </div>
          <SouthernAfricaMap shipments={mergedShips} cities={cityData} height={520} onSelect={setSelected}
            routeGeometry={routePlan?.geometry || []}
            routeStops={routePlan ? [{ city: routePlan.depot }, ...routePlan.stops.map((s) => ({ city: s.city, eta: s.eta, breach: s.breach }))] : []}
            testid="control-tower-map" />
          <div className="mt-3 flex flex-wrap gap-3">
            {DIST.map((d) => (
              <div key={d.k} className="flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: d.c }} />
                <span className="font-semibold">{data.distribution[d.k]}</span>
                <span className="text-muted-foreground">{d.l}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-6">
          {selected && (
            <div className="rounded-xl border border-primary bg-accent p-4" data-testid="ct-selected">
              <p className="font-mono font-bold text-sm">{selected.tracking_number}</p>
              <p className="text-sm text-muted-foreground">{selected.origin} → {selected.destination}</p>
              <div className="mt-2 flex items-center justify-between">
                <StatusBadge status={selected.status} />
                <button className="text-xs text-primary font-semibold" onClick={() => nav(`/app/shipments/${selected.id}`)}>Open →</button>
              </div>
            </div>
          )}
          <div className="rounded-xl border border-border bg-card">
            <div className="p-4 border-b border-border flex items-center gap-2"><Activity size={18} className="text-primary" /><h2 className="font-heading font-bold">Live alert stream</h2></div>
            <div className="max-h-[420px] overflow-y-auto scrollbar-thin divide-y divide-border">
              {data.alerts.length === 0 && <p className="p-4 text-sm text-muted-foreground">No active exceptions.</p>}
              {data.alerts.map((a, i) => (
                <div key={i} className="p-4" data-testid="ct-alert">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-sm font-semibold">{a.tracking_number}</span>
                    <StatusBadge status={a.type} />
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">{a.note}</p>
                  <p className="text-xs text-muted-foreground mt-1">{a.route} · {fmt(a.time)}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
